import React from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import useHttp from '../../hooks/http.hook';
import '../business/Business.css';
import './OrdersPage.css';
import CreateBusinessHeader from '../business/components/CreateBusinessHeader';
import {
  buildStatusFilterOptions,
  getProviderStatusLabel,
  sortCustomOrderStatuses,
  statusBadgeClassName,
} from '../../types/OrderStatusTypes';
import { OrderTypes, isExpenseOrder, isInvestmentOrder, isReplenishOrder } from '../../types/OrderTypes';
import { PaymentStatusTypes } from '../../types/PaymentStatusTypes';
import DateRangeCalendarModal, {
  formatDateFilterLabel,
} from './components/DateRangeCalendarModal';
import OrderFilterSelectModal from './components/OrderFilterSelectModal';
import ModalFrame from '../dashboard/components/product_item_components/ModalFrame';
import OrdersBoard from './components/OrdersBoard';
import {
  DETAILS_COLUMN_BY_ID,
  readDetailsColumnOrder,
  readOrdersViewType,
  writeOrdersViewType,
} from './detailsColumns';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;

const TYPE_FILTER_OPTIONS = [
  { id: OrderTypes.SALES, label: 'продажи' },
  { id: OrderTypes.REPLENISH, label: 'пополнения' },
  { id: OrderTypes.EXPENSES, label: 'расходы' },
  { id: OrderTypes.INVESTMENT, label: 'инвестиции' },
];
const VALID_TYPE_IDS = new Set(TYPE_FILTER_OPTIONS.map((item) => item.id));
const PAYMENT_LABELS = PaymentStatusTypes.labels;

function parseTypeFilterParam(value) {
  const n = Number.parseInt(String(value ?? ''), 10);
  return VALID_TYPE_IDS.has(n) ? n : OrderTypes.SALES;
}

function parseStatusIdsParam(value) {
  if (!value) return [];
  return String(value)
    .split(',')
    .map((id) => Number.parseInt(id.trim(), 10))
    .filter((id) => Number.isFinite(id));
}

function parseDateParam(value) {
  const raw = String(value ?? '').trim();
  return /^\d{4}-\d{2}-\d{2}$/.test(raw) ? raw : null;
}

function applyOrdersFilterParams(prev, updates) {
  const next = new URLSearchParams(prev);
  Object.entries(updates).forEach(([key, value]) => {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
      next.delete(key);
      return;
    }
    next.set(key, Array.isArray(value) ? value.join(',') : String(value));
  });
  if (next.get('type') === String(OrderTypes.SALES)) {
    next.delete('type');
  }
  if (!next.get('dateFrom')) {
    next.delete('dateTo');
  }
  return next;
}

function formatTypeFilterLabel(typeId) {
  const option = TYPE_FILTER_OPTIONS.find((item) => item.id === typeId) ?? TYPE_FILTER_OPTIONS[0];
  return `Тип операции: ${option.label}`;
}

function formatStatusFilterLabel(statusIds, options) {
  if (!statusIds.length) return 'Статусы';
  const labels = (options || [])
    .filter((item) => statusIds.includes(item.id))
    .map((item) => item.label);
  return `Статусы (${labels.join(', ')})`;
}

function formatTotalPrice(value) {
  const n = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(n)) return '0.00 сом';
  return `${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} сом`;
}

function displayCustomerName(row) {
  if (isReplenishOrder(row) || isExpenseOrder(row) || isInvestmentOrder(row)) return '—';
  const name = String(row?.customerName ?? row?.customer_name ?? '').trim();
  return name || '—';
}

function displayCustomerPhone(row) {
  if (isReplenishOrder(row) || isExpenseOrder(row) || isInvestmentOrder(row)) return '—';
  const phone = String(row?.customerPhone ?? row?.customer_phone ?? '').trim();
  if (!phone || phone === '-') return '—';
  return phone;
}

function displayCustomerDescription(row) {
  const text = String(row?.customerDescription ?? row?.customer_description ?? '').trim();
  if (!text || text === '-') return '—';
  return text;
}

function formatQuantity(value) {
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  if (!Number.isFinite(n)) return '0';
  return String(n);
}

function OrdersViewIcon({ view }) {
  const horizontal = view === 'details'
    ? [
        { y: 6.15, h: 1 },
        { y: 8.85, h: 1 },
        { y: 11.55, h: 1 },
        { y: 14.25, h: 1 },
        { y: 16.95, h: 1 },
      ]
    : [
        { y: 6.35, h: 2.6 },
        { y: 10.7, h: 2.6 },
        { y: 15.05, h: 2.6 },
      ];
  const vertical = [
    { x: 6.15, w: 2.4 },
    { x: 10.8, w: 2.4 },
    { x: 15.45, w: 2.4 },
  ];

  return (
    <svg className="shop-create-headerBackIcon" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <rect
        x="3.75"
        y="3.25"
        width="16.5"
        height="17.5"
        rx="2.25"
        stroke="currentColor"
        strokeWidth="1.6"
      />
      {view === 'board'
        ? vertical.map((bar) => (
            <rect key={bar.x} x={bar.x} y="6.35" width={bar.w} height="11.3" rx="0.45" fill="currentColor" />
          ))
        : horizontal.map((bar) => (
            <rect key={bar.y} x="6.5" y={bar.y} width="11" height={bar.h} rx="0.45" fill="currentColor" />
          ))}
    </svg>
  );
}

function orderRowTitle(row) {
  const id = row?.orderId ?? row?.order_id ?? row?.id ?? row?._id;
  return `№ ${id}`;
}

function renderDetailsCellContent(columnId, line, customStatuses) {
  const statusId = line?.statusId ?? line?.status_id;
  const paymentStatusId = line?.paymentStatusId ?? line?.payment_status_id ?? PaymentStatusTypes.NOT_PAID;
  const isSystem = isReplenishOrder(line) || isExpenseOrder(line) || isInvestmentOrder(line);

  switch (columnId) {
    case 'order':
      return orderRowTitle(line);
    case 'product':
      return String(line?.productName ?? line?.product_name ?? '').trim() || '—';
    case 'quantity':
      return formatQuantity(line?.quantity);
    case 'price':
      return formatTotalPrice(line?.linePrice ?? line?.line_price);
    case 'customer':
      return displayCustomerName(line);
    case 'phone':
      return displayCustomerPhone(line);
    case 'comment':
      return displayCustomerDescription(line);
    case 'payment':
      if (isSystem) {
        return <span className="orders-page-itemPaymentMuted">—</span>;
      }
      return (
        <span className={`orders-page-itemPayment orders-page-itemPayment--${paymentStatusId}`}>
          {PAYMENT_LABELS[paymentStatusId] ?? `Оплата ${paymentStatusId}`}
        </span>
      );
    case 'status':
      return (
        <span className={statusBadgeClassName('orders-page-itemStatus', statusId)}>
          {getProviderStatusLabel(statusId, customStatuses)}
        </span>
      );
    default:
      return null;
  }
}

function getOrderDateKey(order) {
  const raw = order?.createdAt ?? order?.created_at;
  if (!raw) return '';
  const d = typeof raw === 'string' ? new Date(raw) : raw;
  if (Number.isNaN(d.getTime())) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function formatDateLabel(dateKey) {
  if (!dateKey) return dateKey;
  const [y, m, d] = dateKey.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  const today = new Date();
  const isToday = date.getDate() === today.getDate() &&
    date.getMonth() === today.getMonth() &&
    date.getFullYear() === today.getFullYear();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const isYesterday = date.getDate() === yesterday.getDate() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getFullYear() === yesterday.getFullYear();
  if (isToday) return 'Сегодня';
  if (isYesterday) return 'Вчера';
  return date.toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
}

function readJournalView(searchParams) {
  const view = searchParams.get('view');
  if (view === 'board') return 'board';
  if (view === 'details' || searchParams.get('details') === '1') return 'details';
  if (searchParams.has('view') || searchParams.has('details')) return 'orders';
  return readOrdersViewType();
}

function groupOrdersByDate(orders) {
  const byDate = {};
  orders.forEach((order) => {
    const key = getOrderDateKey(order);
    if (!key) return;
    if (!byDate[key]) byDate[key] = [];
    byDate[key].push(order);
  });
  const keys = Object.keys(byDate).sort((a, b) => b.localeCompare(a));
  return keys.map((key) => ({
    dateKey: key,
    dateLabel: formatDateLabel(key),
    orders: byDate[key],
  }));
}

function OrdersPage() {
  const navigate = useNavigate();
  const { branchId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { requestWithMeta } = useHttp();

  const [items, setItems] = React.useState([]);
  const [page, setPage] = React.useState(1);
  const [totalPages, setTotalPages] = React.useState(1);
  const [loading, setLoading] = React.useState(true);
  const [loadingMore, setLoadingMore] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [typeModalOpen, setTypeModalOpen] = React.useState(false);
  const [statusModalOpen, setStatusModalOpen] = React.useState(false);
  const [createMenuOpen, setCreateMenuOpen] = React.useState(false);
  const [customStatuses, setCustomStatuses] = React.useState([]);
  const [statusesReady, setStatusesReady] = React.useState(false);
  const selectedTypeId = parseTypeFilterParam(searchParams.get('type'));
  const selectedStatusKey = searchParams.get('statusIds') || '';
  const selectedStatusIds = React.useMemo(
    () => parseStatusIdsParam(selectedStatusKey),
    [selectedStatusKey]
  );
  const dateFrom = parseDateParam(searchParams.get('dateFrom'));
  const dateTo = parseDateParam(searchParams.get('dateTo')) || dateFrom;
  const searchQuery = String(searchParams.get('search') ?? '').trim();
  const journalView = readJournalView(searchParams);
  const detailsView = journalView === 'details';
  const boardView = journalView === 'board';

  React.useEffect(() => {
    writeOrdersViewType(journalView);
  }, [journalView]);
  const [searchInput, setSearchInput] = React.useState(
    () => String(searchParams.get('search') ?? '')
  );
  const [detailsColumnOrder] = React.useState(readDetailsColumnOrder);

  const contentRef = React.useRef(null);
  const sentinelRef = React.useRef(null);
  const searchDebounceRef = React.useRef(null);

  const hasMore = page < totalPages;
  const dateGroups = React.useMemo(() => groupOrdersByDate(items), [items]);
  const statusFilterOptions = React.useMemo(
    () => buildStatusFilterOptions(customStatuses),
    [customStatuses]
  );
  const allStatusIds = React.useMemo(
    () => statusFilterOptions.map(({ id }) => id),
    [statusFilterOptions]
  );
  const dateFilterKey = `${dateFrom || ''}|${dateTo || ''}`;
  const dateFilterSelected = Boolean(dateFrom);
  const typeFilterSelected = selectedTypeId !== OrderTypes.SALES;
  const statusFilterSelected = selectedStatusIds.length > 0;
  const boardStatuses = React.useMemo(() => {
    if (!selectedStatusIds.length) return statusFilterOptions;
    const selected = new Set(selectedStatusIds);
    return statusFilterOptions.filter((item) => selected.has(item.id));
  }, [statusFilterOptions, selectedStatusIds]);
  const viewLabel = journalView === 'board'
    ? 'Колонки статусов'
    : journalView === 'details'
      ? 'Детальный вид'
      : 'Заказы';
  const ordersSearch = searchParams.toString();

  const setFilterParams = React.useCallback((updates) => {
    setSearchParams((prev) => applyOrdersFilterParams(prev, updates), { replace: true });
  }, [setSearchParams]);

  React.useEffect(() => () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
  }, []);

  React.useEffect(() => {
    setSearchInput((prev) => (prev === searchQuery ? prev : searchQuery));
  }, [searchQuery]);

  const handleSearchChange = (value) => {
    setSearchInput(value);
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => {
      setFilterParams({ search: String(value ?? '').trim() });
    }, SEARCH_DEBOUNCE_MS);
  };

  const handleSearchClear = () => {
    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    setSearchInput('');
    setFilterParams({ search: '' });
  };

  const openOrder = (id) => {
    navigate({
      pathname: `/branch/${branchId}/orders/${id}`,
      search: ordersSearch,
    });
  };

  const fetchPage = React.useCallback(
    async (pageNum) => {
      const params = new URLSearchParams({
        page: String(pageNum),
        limit: String(PAGE_SIZE),
      });
      if (selectedStatusIds.length > 0) {
        params.set('statusIds', selectedStatusIds.join(','));
      }
      params.set('typeIds', String(selectedTypeId));
      if (dateFrom) {
        params.set('dateFrom', dateFrom);
        params.set('dateTo', dateTo || dateFrom);
      }
      if (searchQuery) {
        params.set('search', searchQuery);
      }
      const path = detailsView
        ? `/operator/branches/${encodeURIComponent(branchId)}/orders/details?${params.toString()}`
        : `/operator/branches/${encodeURIComponent(branchId)}/orders?${params.toString()}`;
      const res = await requestWithMeta(path, 'GET');
      if (!res?.ok) {
        return { ok: false, rows: [], totalPages: 1 };
      }
      const list = detailsView
        ? (Array.isArray(res?.data?.orderLines) ? res.data.orderLines : [])
        : (Array.isArray(res?.data?.orders) ? res.data.orders : []);
      const pagination = res?.data?.pagination ?? {};
      return {
        ok: true,
        rows: list,
        totalPages: pagination.totalPages ?? 1,
      };
    },
    [branchId, requestWithMeta, selectedStatusIds, selectedTypeId, dateFrom, dateTo, searchQuery, detailsView]
  );

  React.useEffect(() => {
    let cancelled = false;
    setStatusesReady(false);
    (async () => {
      try {
        const res = await requestWithMeta('/operator/custom-order-statuses', 'GET');
        if (cancelled) return;
        const list = Array.isArray(res?.data?.customOrderStatuses)
          ? res.data.customOrderStatuses
          : [];
        setCustomStatuses(sortCustomOrderStatuses(list));
      } catch (_) {
        if (!cancelled) setCustomStatuses([]);
      } finally {
        if (!cancelled) setStatusesReady(true);
      }
    })();
    return () => { cancelled = true; };
  }, [requestWithMeta]);

  React.useEffect(() => {
    if (!branchId) {
      navigate('/branch/store', { replace: true });
      return;
    }
    if (!statusesReady) return;
    if (boardView) {
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    setPage(1);
    setItems([]);
    setTotalPages(1);

    (async () => {
      try {
        const result = await fetchPage(1);
        if (cancelled) return;
        if (!result.ok) {
          setError(detailsView ? 'Не удалось загрузить детали заказов' : 'Не удалось загрузить заказы');
          setItems([]);
          return;
        }
        setItems(result.rows);
        setTotalPages(result.totalPages);
      } catch (_) {
        if (!cancelled) {
          setError('Ошибка загрузки');
          setItems([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => { cancelled = true; };
  }, [branchId, navigate, fetchPage, selectedStatusKey, selectedTypeId, dateFilterKey, searchQuery, statusesReady, detailsView, boardView]);

  React.useEffect(() => {
    const root = contentRef.current;
    const sentinel = sentinelRef.current;
    if (boardView || !root || !sentinel || !hasMore) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [e] = entries;
        if (!e?.isIntersecting) return;
        if (loading || loadingMore || page >= totalPages) return;

        setLoadingMore(true);
        fetchPage(page + 1)
          .then((result) => {
            if (!result.ok) return;
            setItems((prev) => [...prev, ...result.rows]);
            setPage((p) => p + 1);
            setTotalPages(result.totalPages);
          })
          .finally(() => setLoadingMore(false));
      },
      { root, rootMargin: '100px', threshold: 0 }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [boardView, hasMore, loading, loadingMore, page, totalPages, fetchPage]);

  const handleBack = () => {
    navigate(`/branch/${branchId}`);
  };

  const handleApplyDateFilter = ({ start, end }) => {
    setFilterParams({
      dateFrom: start || null,
      dateTo: end || start || null,
    });
  };

  const handleApplyTypeFilter = (ids) => {
    setFilterParams({ type: ids[0] ?? OrderTypes.SALES });
  };

  const handleApplyStatusFilter = (ids) => {
    const unique = [...new Set(ids)];
    const isAll =
      unique.length === 0 ||
      (unique.length === allStatusIds.length && allStatusIds.every((id) => unique.includes(id)));
    setFilterParams({ statusIds: isAll ? [] : unique });
  };

  const handleCycleView = () => {
    const next = journalView === 'orders'
      ? 'details'
      : journalView === 'details'
        ? 'board'
        : 'orders';
    writeOrdersViewType(next);
    if (next === 'orders') {
      setLoading(true);
      setFilterParams({ view: null, details: null });
      return;
    }
    setFilterParams({ view: next, details: null });
  };

  const openDetailsColumnOrder = () => {
    navigate({
      pathname: `/branch/${branchId}/orders/columns`,
      search: ordersSearch,
    });
  };

  return (
    <div className="orders-page-root">
      <CreateBusinessHeader
        title="Журнал 🗒️"
        subtitle=""
        onBack={handleBack}
        right={(
          <button
            type="button"
            className="shop-create-headerAction"
            onClick={handleCycleView}
            aria-label={viewLabel}
            title={viewLabel}
          >
            <OrdersViewIcon view={journalView} />
          </button>
        )}
      />

      <main ref={contentRef} className={`orders-page-content${boardView ? ' orders-page-content--board' : ''}${detailsView ? ' orders-page-content--details' : ''}`}>
        <div className="orders-page-search">
          <svg
            className="orders-page-searchIcon"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
            <path
              d="M20 20l-3.2-3.2"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
          <input
            type="search"
            className="orders-page-searchInput"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder="Имя или телефон"
            aria-label="Поиск по имени или телефону"
            autoComplete="off"
            enterKeyHint="search"
          />
          {searchInput ? (
            <button
              type="button"
              className="orders-page-searchClear"
              onClick={handleSearchClear}
              aria-label="Очистить поиск"
            >
              ×
            </button>
          ) : null}
        </div>

        <div className="orders-page-statusFilters" role="group" aria-label="Фильтр заказов">
          <button
            type="button"
            className={`orders-page-statusFilterTag${dateFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={dateFilterSelected}
            onClick={() => setCalendarOpen(true)}
          >
            {formatDateFilterLabel(dateFrom, dateTo)}
          </button>
          <button
            type="button"
            className={`orders-page-statusFilterTag${typeFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={typeFilterSelected}
            onClick={() => setTypeModalOpen(true)}
          >
            {formatTypeFilterLabel(selectedTypeId)}
          </button>
          <button
            type="button"
            className={`orders-page-statusFilterTag${statusFilterSelected ? ' is-selected' : ''}`}
            aria-pressed={statusFilterSelected}
            onClick={() => setStatusModalOpen(true)}
          >
            {formatStatusFilterLabel(selectedStatusIds, statusFilterOptions)}
          </button>
        </div>

        {boardView ? (
          <OrdersBoard
            branchId={branchId}
            statuses={boardStatuses}
            columnOrder={detailsColumnOrder}
            typeId={selectedTypeId}
            dateFrom={dateFrom}
            dateTo={dateTo}
            searchQuery={searchQuery}
            onOpenOrder={openOrder}
            onOpenColumns={openDetailsColumnOrder}
            requestWithMeta={requestWithMeta}
            statusesReady={statusesReady}
          />
        ) : loading ? (
          <div className="orders-page-loading" aria-busy="true">
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
            <div className="orders-page-skeleton orders-page-skeleton--block" />
          </div>
        ) : error ? (
          <div className="orders-page-error">{error}</div>
        ) : dateGroups.length === 0 ? (
          <div className="orders-page-empty">{searchQuery ? 'Ничего не найдено' : (detailsView ? 'Нет позиций' : 'Нет заказов')}</div>
        ) : (
          <>
            <div className={detailsView ? 'orders-page-detailsGroups' : undefined}>
            {dateGroups.map((group, groupIndex) => (
              <section key={group.dateKey} className="orders-page-dateSection">
                <h2 className="orders-page-dateHeading">{group.dateLabel}</h2>
                <div className={`orders-page-tableWrap${detailsView ? ' orders-page-tableWrap--details' : ''}`}>
                  {detailsView ? (
                    <table className="orders-page-table orders-page-table--details" aria-label={`Позиции за ${group.dateLabel}`}>
                      <thead
                        className={groupIndex === 0 ? 'orders-page-detailsHead--clickable' : undefined}
                        onClick={groupIndex === 0 ? openDetailsColumnOrder : undefined}
                        onKeyDown={groupIndex === 0 ? (event) => {
                          if (event.key === 'Enter' || event.key === ' ') {
                            event.preventDefault();
                            openDetailsColumnOrder();
                          }
                        } : undefined}
                        tabIndex={groupIndex === 0 ? 0 : undefined}
                        role={groupIndex === 0 ? 'button' : undefined}
                        title={groupIndex === 0 ? 'Настроить порядок колонок' : undefined}
                        aria-label={groupIndex === 0 ? 'Настроить порядок колонок' : undefined}
                      >
                        <tr>
                          {detailsColumnOrder.map((columnId) => {
                            const column = DETAILS_COLUMN_BY_ID[columnId];
                            if (!column) return null;
                            return (
                              <th key={columnId} scope="col">
                                {column.label}
                              </th>
                            );
                          })}
                        </tr>
                      </thead>
                      <tbody>
                        {group.orders.map((line) => {
                          const lineId = line?.id ?? line?._id;
                          const orderId = line?.orderId ?? line?.order_id ?? lineId;

                          return (
                            <tr
                              key={lineId}
                              className="orders-page-row orders-page-detailsRow"
                              tabIndex={0}
                              onClick={() => openOrder(orderId)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openOrder(orderId);
                                }
                              }}
                            >
                              {detailsColumnOrder.map((columnId) => {
                                const column = DETAILS_COLUMN_BY_ID[columnId];
                                if (!column) return null;
                                return (
                                  <td
                                    key={columnId}
                                    className={column.className || undefined}
                                    data-label={column.label}
                                  >
                                    {renderDetailsCellContent(columnId, line, customStatuses)}
                                  </td>
                                );
                              })}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <table className="orders-page-table" aria-label={`Заказы за ${group.dateLabel}`}>
                      <thead>
                        <tr>
                          <th scope="col">Заказ</th>
                          <th scope="col" className="orders-page-colDesktop">Клиент</th>
                          <th scope="col" className="orders-page-colDesktop">Сумма</th>
                          <th scope="col">Оплата</th>
                          <th scope="col">Статус</th>
                        </tr>
                      </thead>
                      <tbody>
                        {group.orders.map((order) => {
                          const id = order?.id ?? order?._id;
                          const statusId = order?.statusId ?? order?.status_id;
                          const statusLabel = getProviderStatusLabel(statusId, customStatuses);
                          const paymentStatusId = order?.paymentStatusId ?? order?.payment_status_id ?? PaymentStatusTypes.NOT_PAID;
                          const isReplenishment = isReplenishOrder(order);
                          const isExpense = isExpenseOrder(order);
                          const isInvestment = isInvestmentOrder(order);
                          const isSystem = isReplenishment || isExpense || isInvestment;
                          const paymentLabel = isSystem
                            ? '—'
                            : (PAYMENT_LABELS[paymentStatusId] ?? `Оплата ${paymentStatusId}`);
                          const title = `№ ${id}`;
                          const customerName = isSystem
                            ? '—'
                            : (order?.customerName ?? order?.customer_name ?? '—');
                          const totalLabel = isReplenishment
                            ? '—'
                            : formatTotalPrice(order?.totalPrice ?? order?.total_price);

                          return (
                            <tr
                              key={id}
                              className="orders-page-row"
                              tabIndex={0}
                              onClick={() => openOrder(id)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault();
                                  openOrder(id);
                                }
                              }}
                            >
                              <td className="orders-page-itemId">{title}</td>
                              <td className="orders-page-colDesktop orders-page-itemCustomer">{customerName}</td>
                              <td className="orders-page-colDesktop orders-page-itemTotal">
                                {totalLabel}
                              </td>
                              <td>
                                {isSystem ? (
                                  <span className="orders-page-itemPaymentMuted">—</span>
                                ) : (
                                  <span className={`orders-page-itemPayment orders-page-itemPayment--${paymentStatusId}`}>
                                    {paymentLabel}
                                  </span>
                                )}
                              </td>
                              <td>
                                <span className={statusBadgeClassName('orders-page-itemStatus', statusId)}>
                                  {statusLabel}
                                </span>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  )}
                </div>
              </section>
            ))}
            </div>
            <div ref={sentinelRef} className="orders-page-sentinel" aria-hidden="true" />
            {loadingMore ? (
              <div className="orders-page-loadingMore" aria-busy="true">
                <div className="orders-page-skeleton orders-page-skeleton--block" />
                <div className="orders-page-skeleton orders-page-skeleton--block" />
              </div>
            ) : null}
          </>
        )}
      </main>

      <footer className="orders-page-footerRow">
        <button
          type="button"
          className="orders-page-footerCreate"
          aria-label="Создать расход или инвестицию"
          onClick={() => setCreateMenuOpen(true)}
        >
          <svg viewBox="0 0 24 24" width="22" height="22" fill="none" aria-hidden="true">
            <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
          </svg>
        </button>
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary orders-page-footerLedger"
          onClick={() => navigate(`/branch/${branchId}/orders/investment`)}
        >
          + Инвестиция
        </button>
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary orders-page-footerLedger"
          onClick={() => navigate(`/branch/${branchId}/orders/expense`)}
        >
          + Расход
        </button>
        <button
          type="button"
          className="orders-page-footerButton orders-page-footerButtonPrimary"
          onClick={() => navigate(`/branch/${branchId}/orders/create`)}
        >
          Оформить заказ
        </button>
      </footer>

      {createMenuOpen ? (
        <ModalFrame title="Создать" onClose={() => setCreateMenuOpen(false)}>
          <div className="main-page-modalList">
            <button
              type="button"
              className="main-page-modalItem"
              onClick={() => navigate(`/branch/${branchId}/orders/expense`)}
            >
              Расход
            </button>
            <button
              type="button"
              className="main-page-modalItem"
              onClick={() => navigate(`/branch/${branchId}/orders/investment`)}
            >
              Инвестиция
            </button>
          </div>
        </ModalFrame>
      ) : null}

      <DateRangeCalendarModal
        isOpen={calendarOpen}
        onClose={() => setCalendarOpen(false)}
        initialStart={dateFrom}
        initialEnd={dateTo}
        onApply={handleApplyDateFilter}
      />
      <OrderFilterSelectModal
        isOpen={typeModalOpen}
        title="Тип операции"
        options={TYPE_FILTER_OPTIONS}
        selectedIds={[selectedTypeId]}
        resetIds={[OrderTypes.SALES]}
        onApply={handleApplyTypeFilter}
        onClose={() => setTypeModalOpen(false)}
      />
      <OrderFilterSelectModal
        isOpen={statusModalOpen}
        title="Статусы"
        options={statusFilterOptions}
        selectedIds={selectedStatusIds}
        multiple
        colorizeStatus
        resetIds={allStatusIds}
        onApply={handleApplyStatusFilter}
        onClose={() => setStatusModalOpen(false)}
      />
    </div>
  );
}

export default OrdersPage;
